import { useCallback } from 'react'
import { Execute, RecaptchaAction, RecaptchaConfig } from '@lib/types'
import { init, grecaptcha, waitTillReCaptchaReady } from '../utils/recaptcha'
import { getLogger } from '../tracking/felog/telemetry'

const MAX_RETRIES = 3

export const useRecaptchaHook = (recaptchaConfig: RecaptchaConfig) => {
  const logger = getLogger('cms.recaptcha')
  const siteKey = recaptchaConfig.siteKey

  const execute: Execute = useCallback(
    async (action: RecaptchaAction, retries = 0) => {
      try {
        const requestTime = Date.now()
        await waitTillReCaptchaReady()
        const readyTime = Date.now()

        logger.info('Captcha ready time ', {
          action,
          timeSinceRequest: readyTime - requestTime,
        })

        const newToken = (await grecaptcha()?.enterprise.execute(siteKey, { action })) || undefined

        if (!newToken) {
          if (retries < MAX_RETRIES) {
            logger.warn('reCAPTCHA execution failed, retrying', { action, retries: retries + 1 })
            return await execute(action, retries + 1)
          } else {
            throw new Error('reCAPTCHA execution failed, max retries reached')
          }
        }

        logger.info('reCAPTCHA executed successfully, new token obtained', { action })

        return newToken
      } catch (err) {
        logger.error('Error executing recaptcha', { action, error: JSON.stringify(err) })
        if (retries >= MAX_RETRIES) {
          logger.error('reCAPTCHA execution failed, max retries reached', { action })
        }

        if (!err && retries < MAX_RETRIES) {
          return await execute(action, retries + 1)
        }

        return undefined
      }
    },
    [siteKey, logger]
  )

  init()

  return execute
}
